import React, { useMemo, useContext } from 'react';
import PropTypes from 'prop-types';
import { PerformanceMarkerProvider } from '@bingads-webui-campaign-react/performance-marker-provider';
import { Scenario, Activity } from '@bingads-webui-react/hoc-with-signal';
import { WizardPerfMarker } from '@bingads-webui-campaign/wizard-performance-marker';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import {
  Wizard,
  LayoutContainer,
  Layout,
  StepContainer,
} from '@bingads-webui-react/wizard';
import {
  BusyBlocker,
  CancelWizardDialog,
  DefaultTemplate,
} from '@bingads-webui-react/wizard-ui-library';
import { withLoggingContext } from '@bingads-webui-campaign-react/copilot-loggers';
import { withRecommendationContext } from '@bingads-webui-campaign-react/perf-max-recommendation';
import { FluentProvider, webLightTheme } from '@fluentui/react-components';
import { DisplayAdsWithCopilot } from '../ad-components';
import { useDisplayAdsRepository } from './use-display-ads-repository';
import { EditAdFooter } from './edit-ad-footer';

const DISPLAY_AD = 'Display Ad';

const StepConfig = [{
  id: DISPLAY_AD,
  view: props => (
    <Activity name="EditDisplayAd">
      <DisplayAdsWithCopilot {...props} isAdGridEditorMode />
    </Activity>
  ),
  repository: useDisplayAdsRepository,
}];

const EditDisplayAdWizardInternal = ({
  gridData,
  perfMarker,
  onCancel,
  onSave,
  onSaveAndCreateAnother,
  isMultiAdFormatAd,
}) => {
  const instrumentation = useContext(InstrumentationContext);

  const wizardPerfMarker = useMemo(() => (
    perfMarker.createChild('DisplayAdEditAdWizard')
  ), [perfMarker]);

  const config = useMemo(() => ({
    id: 'display-plus-wizard',
    initialStep: DISPLAY_AD,
    steps: StepConfig,
    actions: {
      onDone: () => { },
      onExited: () => onCancel(),
    },
    initialWizardData: {
      savedAdModel: gridData,
      onSave,
      onSaveAndCreateAnother,
      isMultiAdFormatAd,
    },
    attachStepActivities: false,
  }), [gridData, onCancel, onSave, onSaveAndCreateAnother, isMultiAdFormatAd]);

  return (
    <PerformanceMarkerProvider
      name="DisplayAdEditAdWizard"
      instrumentation={instrumentation}
      immediateDone={false}
    >
      <FluentProvider theme={webLightTheme}>
        <Scenario name="DisplayAdEditAdWizard">
          <Activity name="DefaultWizardActivity">
            <Wizard config={config}>
              <WizardPerfMarker wizardName="DisplayPlusWizard" wizardMarker={wizardPerfMarker} >
                <LayoutContainer template={DefaultTemplate}>
                  <Layout type="StepContainer" component={StepContainer} />
                  <Layout type="Blocker" component={BusyBlocker} loadingText="Processing" />
                  <Layout type="CancelDialog" component={CancelWizardDialog} />
                  <Layout type="Footer" component={EditAdFooter} />
                </LayoutContainer>
              </WizardPerfMarker>
            </Wizard>
          </Activity>
        </Scenario>
      </FluentProvider>
    </PerformanceMarkerProvider>
  );
};

EditDisplayAdWizardInternal.propTypes = {
  gridData: PropTypes.object.isRequired,
  perfMarker: PropTypes.object.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onSaveAndCreateAnother: PropTypes.func.isRequired,
  isMultiAdFormatAd: PropTypes.bool,
};

EditDisplayAdWizardInternal.defaultProps = {
  isMultiAdFormatAd: false,
};

export const EditDisplayAdWizard = withLoggingContext(withRecommendationContext(EditDisplayAdWizardInternal));
